<template>
  <div class="mt-2">
    <div >
      <m-form class="bg-gray-100 border rounded">
        <h2 class="font-bold">Add Transaction</h2>
        <div class="flex items-start space-x-2">
          <div class="mt-1 w-full">
            <m-datepicker
              v-model="$v.form.date.$model"
              :state="$v.$dirty && $v.form.date.$dirty ? $v.form.date.$invalid ? 'error' : '' : ''"
              placeholder="Select Transaction date..."
            />
            <p
              v-if="$v.$dirty &&  $v.form.date.$dirty && $v.form.date.$invalid"
              class="mt-1 text-red-500 text-sm leading-tight"
            >
              <!-- eslint-disable-next-line max-len -->
              Date is required and transaction must be on or after 01/07/2017.
            </p>
          </div>
          <div class="w-full">
            <m-select
              v-model="$v.form.event.$model"
              :options="types"
              :state="$v.$dirty &&  $v.form.event.$dirty
                ? $v.form.event.$invalid ? 'error' : '' : ''"
              defaultSelect="Select Transaction type..."
            />
            <p
              v-if="$v.$dirty && $v.form.event.$dirty && $v.form.event.$invalid"
              class="mt-1 text-red-500 text-sm leading-tight"
            >
              Transaction type is required.
            </p>
          </div>
          <div class="w-full">
            <m-input-dollar
              id="transaction-amount"
              v-model.number="$v.form.amount.$model"
              :state="$v.$dirty && $v.form.amount.$dirty
                ? $v.form.amount.$invalid ? 'error' : '' : ''"
              placeholder="Transaction value"
            />
            <p
              v-if="$v.$dirty && $v.form.amount.$dirty && $v.form.amount.$invalid"
              class="mt-1 text-red-500 text-sm leading-tight"
            >
              <!-- eslint-disable-next-line max-len -->
              Transaction value is required and must be greater than zero. The transaction will be consider a debit or credit based on the type.
            </p>
          </div>
          <m-button
            class="mt-1 flex items-center"
            @click="handleAddTransaction"
          >
            <span>Add</span>
            <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clip-rule="evenodd"></path></svg>
          </m-button>
        </div>
      </m-form>
      <!-- eslint-disable max-len -->
      <div
        v-if="error !== ''"
        class="mt-4 p-2 rounded bg-red-100 text-red-800 border border-red-200 text-sm flex items-center space-x-2"
      >
      <!-- eslint-enable max-len -->
        <svg class="w-6 h-6 flex-none" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path></svg>
        <p class="leading-tight">{{ error }}</p>
      </div>
      <!-- eslint-disable max-len -->
      <div
        v-if="warn !== ''"
        class="mt-4 p-2 rounded bg-yellow-100 text-yellow-800 border border-yellow-200 text-sm flex items-center space-x-2"
      >
      <!-- eslint-enable max-len -->
        <svg class="w-6 h-6 flex-none" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path></svg>
        <p class="leading-tight">{{ warn }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import { required, minValue } from 'vuelidate/lib/validators';

export const TRANSACTION_TYPES = [
  {
    event: 1,
    type: 'credit',
    option: 'New Pension Commencement',
  },
  {
    event: 2,
    type: 'debit',
    option: 'Lump sum commutation',
  },
  {
    event: 3,
    type: 'debit',
    option: 'Pension Roll back',
  },
  {
    event: 4,
    type: 'credit',
    option: 'Special LRBA amount',
  },
  {
    event: 5,
    type: 'credit',
    option: 'Inheritance of a reversionary pension',
    warn: 'Confirm that the date entered is the date on which the pension will count for TBC purposes - ie, 12 months after the date of death',
  },
  {
    event: 6,
    type: 'credit',
    option: 'TRIS becoming a retirement phase pension',
    warn: 'Confirm that the date entered is the date on which the pension became a retirement phase pension, not the date the pension originally commenced',
  },
  {
    event: 7,
    type: 'credit',
    option: 'Excess transfer balance earnings',
  },
  {
    event: 8,
    type: 'debit',
    option: 'Pension fails the minimum pension rules',
    warn: 'The date should be the end of the financial year in which the failure occurred, even though the Fund lost its tax exemption at the start of the financial year.  Note that a corresponding "New Pension Commencement" entry should be made on the following 1 July (the next day) if the pension continued the following year.',
  },
  {
    event: 9,
    type: 'credit',
    option: 'Other Credit (addition)',
  },
  {
    event: 10,
    type: 'debit',
    option: 'Other Debit (subtraction)',
  },
];

function initForm() {
  return {
    date: '',
    event: '',
    type: '',
    amount: null,
  };
}

export default {
  name: 'transfer-balance-cap-add-transaction',
  data() {
    return {
      types: TRANSACTION_TYPES.map(type => type.option),
      form: initForm(),
      warn: '',
      error: '',
    };
  },
  methods: {
    handleAddTransaction() {
      this.$v.$touch();
      if (!this.$v.form.$error) {
        const today = new Date();
        // eslint-disable-next-line max-len
        this.form.longDate = new Date(this.form.date.getFullYear(), this.form.date.getMonth(), this.form.date.getDate(), today.getHours(), today.getMinutes(), today.getSeconds(), 0);
        this.$emit('add-transaction', this.form);
        this.form = initForm();
        this.$v.$reset();
        this.$mixpanel.track('tbc-add-transaction');
      }
    },
  },
  watch: {
    form: {
      deep: true,
      handler(value) {
        const transactionType = TRANSACTION_TYPES.filter(type => type.option === value.event)[0];
        this.form.type = transactionType ? transactionType.type : '';
        this.warn = transactionType && transactionType.warn ? transactionType.warn : '';
        this.error = value.date && value.date < new Date(2017, 6, 1) ? 'For pensions that commenced before 1 July 2017 but were still in place at that time, enter the date as 1 July 2017 and the value of the pension account at that time' : '';
      },
    },
  },
  validations: {
    form: {
      date: {
        required,
        minDate: (value) => {
          if (_.isDate(value)) {
            return value >= new Date(2017, 6, 1);
          }
          return new Date(value) >= new Date(2017, 6, 1);
        },
      },
      event: {
        required,
      },
      amount: {
        required,
        min: minValue(1),
      },
    },
  },
};
</script>

<style>

</style>
